import React, { useState } from 'react';

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: "Where do I fly in to?",
      answer: "If you're coming from outside India, the best place to fly in to is Delhi. From Delhi, there are direct flights to Khajuraho. We recommend booking your tickets to Delhi separate to the tickets to Khajuraho, because the Khajuraho tickets are on Indigo Airlines. Tickets to Khajuraho via Indigo are now open."
    },
    {
      question: "What time should I arrive and when can I leave?",
      answer: "Plan to arrive in Khajuraho by the morning/afternoon of March 13, 2026. The final event will conclude late evening on Saturday, March 14, so departing on March 15, 2026 will let you be there for all our celebrations. If you'd like to come early or stay a few extra days, just let us know and we'll help organize!"
    },
    {
      question: "How do I see a tiger?",
      answer: "Reach out to Ishan about this. He'll give you special instructions."
    },
    {
      question: "Where do I stay?",
      answer: "Hotels are on us! Our events team will coordinate the details with you. We've arranged accommodations for all our guests, so you won't need to worry about booking or payment."
    },
    {
      question: "How do I reach the wedding venue?",
      answer: "Our events team will coordinate transportation from Khajuraho Airport and Train station to the hotel once you've RSVPd."
    },
    {
      question: "Who should I send my travel details to?",
      answer: "Our events team will reach out to you over Whatsapp to request these details."
    },
    {
      question: "I'm visiting India for the first time and need a visa. What should I do?",
      answer: {
        type: "list",
        items: [
          "Apply for an e-Tourist Visa for a smooth, hassle-free process.",
          "Apply early - as soon as you book your flights to allow processing time.",
          "Ensure your passport is valid for at least 6 months from your planned arrival date.",
          "If you need an invitation letter for your visa application, please reach out to us and we'll provide one with your details."
        ]
      }
    },
    {
      question: "What do I wear?",
      answer: {
        type: "list",
        intro: "Please view the schedule page for detailed dress code information with example photos!",
        items: [
          "Day 1 (Sangeet): Festive Indian attire",
          "Day 2 (Wedding): Traditional wedding attire",
          "Day 2 (Reception): Black Tie / Western, Indian, or Indo-western Formals"
        ],
        outro: "We've included outfit examples in the \"Schedule\" page to help guide your choices."
      }
    },
    {
      question: "This is my first time buying Indian attire. Where can I shop?",
      answer: "For guests in India - you've got this! For US guests - we recommend online retailers or local stores in NYC/NJ areas. If you need specific recommendations for online stores, sizing guides, or styling tips for your first Indian outfit purchase, please reach out and we'll share our shopping guide."
    },
    {
      question: "Can I bring a +1?",
      answer: "Plus-ones are welcome for guests who received a '& Guest' invitation. If you're unsure about your invitation details, please reach out to us directly."
    },
    {
      question: "What if I have dietary restrictions or allergies?",
      answer: "Please mention any dietary restrictions, allergies, or special dietary needs in your RSVP form. Our catering team can accommodate vegetarian, vegan, gluten-free, and other dietary requirements."
    },
    {
      question: "What's the weather like in March in Khajuraho?",
      answer: "Mid-March in Khajuraho is warm, sunny, and dry, with daytime highs around 33°C (91°F) and cool, pleasant evenings. We recommend bringing a light sweater or jacket for the evenings."
    },
    {
      question: "If my flight is delayed, what should I do?",
      answer: "Message our Events Team on WhatsApp or call the provided number as soon as you know your delay. We'll adjust your pickup as best we can."
    },
    {
      question: "Will there be time for sightseeing?",
      answer: "Yes — there will be free time on Friday morning and Sunday for anyone who'd like to explore Khajuraho's temples and heritage sites."
    }
  ];

  const toggleQuestion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="space-y-24">
      <div className="text-center">
        <h1 className="text-4xl font-display font-medium text-sage-800 mb-4">
          Frequently Asked Questions
        </h1>
        <div className="elegant-divider"></div>
        <p className="text-sage-grey-600">
          Everything you need to know for our celebration.
        </p>
      </div>
      
      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <div key={index} className="glass-card overflow-hidden">
            <button
              onClick={() => toggleQuestion(index)}
              className="w-full p-6 text-left flex items-center justify-between hover:bg-white hover:bg-opacity-20 transition-colors duration-300 min-h-[60px]"
            >
              <h2 className="text-lg font-display text-soft-black pr-4 tracking-wide font-normal">
                {faq.question}
              </h2>
              <div className={`transform transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`}>
                <svg className="w-5 h-5 text-text-gray" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>
            
            <div className={`overflow-hidden transition-all duration-300 ${openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className="px-6 pb-6">
                <div className="pt-2 border-t border-mid-gray">
                  {typeof faq.answer === 'string' ? (
                    <p className="text-text-gray leading-relaxed">
                      {faq.answer}
                    </p>
                  ) : (
                    <div className="text-text-gray leading-relaxed">
                      {faq.answer.intro && (
                        <p className="mb-3">{faq.answer.intro}</p>
                      )}
                      <ol className="list-decimal list-inside space-y-2 mb-3">
                        {faq.answer.items.map((item, itemIndex) => (
                          <li key={itemIndex}>{item}</li>
                        ))}
                      </ol>
                      {faq.answer.outro && (
                        <p>{faq.answer.outro}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Contact Info */}
      <div className="glass-card p-12 text-center">
        <h3 className="font-display text-soft-black mb-8 tracking-wide">
          Still Have Questions?
        </h3>
        <div className="elegant-divider"></div>
        <div className="mt-8">
          <p className="text-text-gray mb-8 leading-relaxed">
            Don't hesitate to reach out! We're here to help make your experience wonderful.
          </p>
          <a 
            href="mailto:shivangiandishan@gmail.com"
            className="minimal-button inline-block"
          >
            Contact Us
          </a>
        </div>
      </div>
    </div>
  );
};

export default FAQ;